import type { AssigneeSource } from "./workspaceAssignees";
export type ProcoreDailyLogCategory =
  | "weather"
  | "delays"
  | "manpower"
  | "notes"
  | "visitors"
  | "phone_calls"
  | "inspections"
  | "deliveries"
  | "dumpsters"
  | "work_logs"
  | "plan_revisions"
  | "productivity"
  | "safety_violations"
  | "accidents"
  | "equipment"
  | "quantities"
  | "waste"
  | "photos"
  | "observations";

export type ProcoreDailyLogScope = "daily_log" | "observation";

export type ProcoreDailyLogGenerationMethod = "ai" | "fallback";

export const PROCORE_INSPECTION_DEFAULT_DURATION_MINUTES = 10;

export type ProcoreDailyLogFallbackReason =
  | "no_provider"
  | "provider_error"
  | "oversize"
  | "max_tokens"
  | "invalid_json"
  | "empty_items"
  | "low_quality";

export type ProcoreDailyLogFieldType =
  | "text"
  | "textarea"
  | "number"
  | "boolean"
  | "time"
  | "date";

export type ProcoreDailyLogFieldValue = string | number | boolean | null;

export type ProcoreSyncStatus =
  | "idle"
  | "pending"
  | "syncing"
  | "synced"
  | "partial"
  | "blocked"
  | "failed"
  | "skipped";

/**
 * Why a stuck record is stuck, and therefore whether a timer can fix it.
 *
 * Mirrors `backend/services/procoreSyncRetry.ts`, which owns the mapping from
 * a `lastError` string to one of these. Declared here because the project
 * page needs it to tell "we will keep trying" from "this needs you".
 */
export type ProcoreRetryClass = "transient" | "config" | "terminal" | "awaiting_user";

export type ProcoreSyncProvenance = "led_ai_authored" | "imported";

export const PROCORE_DELAY_TYPE_OPTIONS = [
  "Existing Conditions",
  "Material",
  "Non-Conformance",
  "Owner Directive",
  "Quality",
  "Safety Event",
  "Weather",
] as const;

export type ProcoreDelayTypeOption = (typeof PROCORE_DELAY_TYPE_OPTIONS)[number];

/**
 * The ONLY status `delay_logs` and `dumpster_logs` accept.
 *
 * Their `status` column is Procore's approval workflow, not a free field:
 * `normalizeApprovalWorkflowStatus` (procoreSync.registry.ts) folds a long
 * synonym list — "active", "open", "in progress", "complete", "cancelled" —
 * into these three and DROPS anything it cannot place. A card that offered a
 * text box here let a reviewer type "Active" and watch it publish as
 * "pending", or vanish, with nothing said.
 *
 * `procoreApprovalStatus.test.ts` pins these against the normaliser so the
 * pick list and the wire cannot drift.
 */
export const PROCORE_APPROVAL_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
] as const;

/** The categories whose `status` IS that workflow. */
export const APPROVAL_WORKFLOW_CATEGORIES: ReadonlySet<string> = new Set(["delays", "dumpsters"]);

// Review-UI status option lists. Shared so the contract tests and the UI cannot drift.
export const OBSERVATION_STATUS_OPTIONS = ["Initiated", "Ready for review", "Not accepted", "Closed"] as const;
/**
 * The observation priorities the reviewer UI offers.
 *
 * Shared for the same reason the status list is, and for one more: the
 * two-way sync's push-read-back guard has to know, for EVERY option a user
 * can pick, whether the publisher would send it or omit it. `Urgent` is
 * offered here but not recognized by `normalizeObservationPriorityForPublish`,
 * so `serializeObservationItem` omits `priority` entirely for it — and a
 * guard that did not know that parked those observations `push_failed`
 * forever (round 9). `procoreSync.registry.test.ts` loops this list to keep
 * that provable rather than remembered.
 */
export const OBSERVATION_PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"] as const;
export const DAILY_LOG_STATUS_OPTIONS = ["Pending", "Approved", "Rejected"] as const;

// Procore's standard default observation types ("Category - Type" labels).
// Used as the canonical fallback list for the observation `type` field — both
// for AI extraction and the reviewer/editor dropdown — whenever a project's own
// Procore observation types are unavailable (e.g. Procore not connected). When a
// site IS Procore-linked, that project's fetched observation types take priority.
export const DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS = [
  "Commissioning - Commissioning",
  "Environmental - Air Quality",
  "Environmental - Cultural Heritage",
  "Environmental - Hazardous Materials",
  "Environmental - Noise",
  "Environmental - Spill",
  "Environmental - Storm Water",
  "Environmental - Vegetation",
  "Environmental - Waste Management",
  "Environmental - Wildlife",
  "Quality - Corrective Action",
  "Quality - Deficiency",
  "Quality - Non-Conformance",
  "Quality - Pre-Punch",
  "Safety - Corrective Action",
  "Safety - Near Miss",
  "Safety - Preventative Action",
  "Safety - Safety Hazard",
  "Safety - Safety Notice",
  "Safety - Safety Violation",
  "Warranty - Service Call",
  "Work to Complete - Work to Complete",
] as const;

export type DefaultProcoreObservationTypeOption =
  (typeof DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS)[number];

/** Display identical category/subtype pairs once; keep the stored sync value intact. */
export function formatObservationType(value: string): string {
  const separator = value.indexOf(" - ");
  if (separator < 0) return value;
  const category = value.slice(0, separator).trim();
  const subtype = value.slice(separator + 3).trim();
  return category && category === subtype ? category : value;
}

export function normalizeProcoreDelayType(value: unknown): ProcoreDelayTypeOption | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase().replace(/[_\s]+/g, " ");
  if (!normalized) {
    return null;
  }

  const exact = PROCORE_DELAY_TYPE_OPTIONS.find(
    (option) => option.toLowerCase() === normalized,
  );
  if (exact) {
    return exact;
  }

  if (/\b(weather|rain|storm|snow|wind|temperature|heat|cold)\b/.test(normalized)) {
    return "Weather";
  }
  if (/\b(material|delivery|supply|equipment|procurement)\b/.test(normalized)) {
    return "Material";
  }
  if (/\bnon[- ]?conformance|nonconformance|defect|rework\b/.test(normalized)) {
    return "Non-Conformance";
  }
  if (/\bowner|directive|change order|client\b/.test(normalized)) {
    return "Owner Directive";
  }
  if (/\bquality|inspection|test|deficiency\b/.test(normalized)) {
    return "Quality";
  }
  if (/\bsafety|incident|accident|hazard|violation\b/.test(normalized)) {
    return "Safety Event";
  }
  if (/\bexisting condition|unforeseen|unknown|concealed|field condition\b/.test(normalized)) {
    return "Existing Conditions";
  }

  return null;
}

// Procore's observed-weather fields are enumerations, not free text: an
// off-list value fails the whole weather log with
// `{"sky":["is not included in the list"]}` (422). These lists are the exact
// option sets from the Observed Weather row of Procore's Daily Log, where each
// item's wire value equals its display label. The UI's "-----" divider between
// the ground and sea conditions is a rendering artifact, not a real option.
export const PROCORE_WEATHER_SKY_OPTIONS = [
  "Clear",
  "Cloudy",
  "Overcast",
  "Fog",
  "Mist",
  "Rain",
  "Snow",
  "Ice/Sleet/Hail",
  "Smoke",
] as const;

export const PROCORE_WEATHER_TEMPERATURE_OPTIONS = [
  "Very Hot",
  "Hot",
  "Mild",
  "Cold",
  "Very Cold",
] as const;

export const PROCORE_WEATHER_WIND_OPTIONS = ["Calm", "Light Wind", "High Wind"] as const;

export const PROCORE_WEATHER_GROUND_OPTIONS = [
  "Dry",
  "Wet/Muddy",
  "Flooded",
  "Snow",
  "Frozen",
  "High Tide",
  "Low Tide",
  "Heavy Surf/Swell",
] as const;

export const PROCORE_WEATHER_CALAMITY_OPTIONS = [
  "Earthquake",
  "Fire",
  "Flash Flood",
  "Landslide",
  "Tornado",
  "Hurricane",
  "Snow",
  "Other",
] as const;

export type ProcoreWeatherSkyOption = (typeof PROCORE_WEATHER_SKY_OPTIONS)[number];
export type ProcoreWeatherTemperatureOption =
  (typeof PROCORE_WEATHER_TEMPERATURE_OPTIONS)[number];
export type ProcoreWeatherWindOption = (typeof PROCORE_WEATHER_WIND_OPTIONS)[number];
export type ProcoreWeatherGroundOption = (typeof PROCORE_WEATHER_GROUND_OPTIONS)[number];
export type ProcoreWeatherCalamityOption = (typeof PROCORE_WEATHER_CALAMITY_OPTIONS)[number];

function normalizeWeatherText(value: unknown): string {
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }
  if (typeof value !== "string") {
    return "";
  }
  return value.trim().toLowerCase().replace(/[_\s]+/g, " ");
}

function matchWeatherOption<T extends string>(
  normalized: string,
  options: readonly T[],
  patterns: readonly (readonly [RegExp, T])[],
): T | null {
  if (!normalized) {
    return null;
  }

  const exact = options.find((option) => option.toLowerCase() === normalized);
  if (exact) {
    return exact;
  }

  for (const [pattern, option] of patterns) {
    if (pattern.test(normalized)) {
      return option;
    }
  }

  return null;
}

// Ordered most-specific first: "freezing rain" is hail, not rain, and
// "overcast" must win before the looser cloud test.
const SKY_PATTERNS = [
  [/\b(ice|icy|sleet|hail|freezing rain)\b/, "Ice/Sleet/Hail"],
  [/\b(snow|snowing|flurr\w*|blizzard)\b/, "Snow"],
  [/\b(smoke|smoky|smokey|haze|hazy|wildfire)\b/, "Smoke"],
  [/\b(fog|foggy)\b/, "Fog"],
  [/\b(mist|misty|drizzl\w*)\b/, "Mist"],
  [/\b(rain|rainy|shower\w*|storm\w*|thunder\w*|downpour|precipitation)\b/, "Rain"],
  [/\b(overcast|gray|grey|dreary|gloomy|dull)\b/, "Overcast"],
  [/\b(cloud\w*)\b/, "Cloudy"],
  [/\b(clear|sunny|sun|blue sky|blue skies|bright)\b/, "Clear"],
] as const satisfies readonly (readonly [RegExp, ProcoreWeatherSkyOption])[];

export function normalizeProcoreWeatherSky(value: unknown): ProcoreWeatherSkyOption | null {
  return matchWeatherOption(
    normalizeWeatherText(value),
    PROCORE_WEATHER_SKY_OPTIONS,
    SKY_PATTERNS,
  );
}

// Ordered so the intensified descriptors are read before their plain forms.
const TEMPERATURE_PATTERNS = [
  [/\b(freezing|frigid|bitter\w*|arctic|sub[- ]?zero|below freezing)\b/, "Very Cold"],
  [/\b(scorching|sweltering|blistering|extreme heat|heat ?wave|boiling)\b/, "Very Hot"],
  [/\b(cold|chilly|frosty)\b/, "Cold"],
  [/\b(mild|moderate|temperate|warm|cool)\b/, "Mild"],
  [/\b(hot|muggy)\b/, "Hot"],
] as const satisfies readonly (readonly [RegExp, ProcoreWeatherTemperatureOption])[];

// A spoken reading, with the unit optional because walkers usually just say
// "78 degrees". Mirrors `deriveWeatherFieldsFromText`, which defaults to °F.
const TEMPERATURE_READING_RE = /(-?\d{1,3})(?:\.\d+)?\s*(?:°\s*)?(c|f|celsius|centigrade|fahrenheit)?\b/;

function bucketTemperature(degreesF: number): ProcoreWeatherTemperatureOption {
  if (degreesF >= 95) return "Very Hot";
  if (degreesF >= 80) return "Hot";
  if (degreesF >= 55) return "Mild";
  if (degreesF >= 33) return "Cold";
  return "Very Cold";
}

/**
 * Procore's `temperature` is a bucket ("Hot"), but LedAI deliberately keeps the
 * precise reading ("78°F") on the walk card. So this returns both: the bucket
 * for `temperature`, and the Fahrenheit number for Procore's `average` column
 * ("the average temperature observed during the workday") so the reading is not
 * lost. `averageF` is null for a Celsius reading — `average` carries no unit of
 * its own, and the project's own unit setting is not visible from here.
 */
export function normalizeProcoreWeatherTemperature(value: unknown): {
  option: ProcoreWeatherTemperatureOption | null;
  averageF: number | null;
} {
  const normalized = normalizeWeatherText(value);
  if (!normalized) {
    return { option: null, averageF: null };
  }

  const exact = PROCORE_WEATHER_TEMPERATURE_OPTIONS.find(
    (option) => option.toLowerCase() === normalized,
  );
  if (exact) {
    return { option: exact, averageF: null };
  }

  const reading = normalized.match(TEMPERATURE_READING_RE);
  if (reading) {
    const stated = Number(reading[1]);
    const isCelsius = /^c/.test(reading[2] || "");
    const degreesF = isCelsius ? stated * (9 / 5) + 32 : stated;
    // Guard against a number that cannot be an ambient reading (an oven temp, a
    // grid line, a stray quantity that landed in the field).
    if (Number.isFinite(degreesF) && degreesF >= -60 && degreesF <= 140) {
      return {
        option: bucketTemperature(degreesF),
        averageF: isCelsius ? null : stated,
      };
    }
  }

  return {
    option: matchWeatherOption(normalized, [], TEMPERATURE_PATTERNS),
    averageF: null,
  };
}

// Calm is tested first so "no wind" is not read as wind.
const WIND_PATTERNS = [
  [/\b(calm|no wind|still|windless|dead air)\b/, "Calm"],
  [/\b(gusty|gusting|gusts?|strong wind\w*|high wind\w*|windy|gale|blustery)\b/, "High Wind"],
  [/\b(light breeze|light wind\w*|breeze|breezy|moderate wind\w*|gentle)\b/, "Light Wind"],
] as const satisfies readonly (readonly [RegExp, ProcoreWeatherWindOption])[];

const WIND_SPEED_RE = /(\d{1,3})\s*(?:mph|miles per hour|knots|kts)\b/;

export function normalizeProcoreWeatherWind(value: unknown): ProcoreWeatherWindOption | null {
  const normalized = normalizeWeatherText(value);
  if (!normalized) {
    return null;
  }

  const exact = PROCORE_WEATHER_WIND_OPTIONS.find(
    (option) => option.toLowerCase() === normalized,
  );
  if (exact) {
    return exact;
  }

  // A measured speed beats a descriptor: "gusts to 30 mph" is high wind even
  // though a bare "gusts" would already say so.
  const speed = normalized.match(WIND_SPEED_RE);
  if (speed) {
    const mph = Number(speed[1]);
    if (mph >= 20) return "High Wind";
    if (mph >= 5) return "Light Wind";
    return "Calm";
  }

  return matchWeatherOption(normalized, [], WIND_PATTERNS);
}

const GROUND_PATTERNS = [
  [/\b(heavy surf|surf|swell)\b/, "Heavy Surf/Swell"],
  [/\bhigh tide\b/, "High Tide"],
  [/\blow tide\b/, "Low Tide"],
  [/\b(flood\w*|standing water|ponding|submerged|inundated)\b/, "Flooded"],
  [/\b(frozen|frost\w*|icy|ice)\b/, "Frozen"],
  [/\b(snow\w*)\b/, "Snow"],
  [/\b(mud|muddy|wet|damp|saturat\w*|soggy|slick|slop\w*)\b/, "Wet/Muddy"],
  [/\b(dry|firm|dusty|hard packed)\b/, "Dry"],
] as const satisfies readonly (readonly [RegExp, ProcoreWeatherGroundOption])[];

export function normalizeProcoreWeatherGround(value: unknown): ProcoreWeatherGroundOption | null {
  return matchWeatherOption(
    normalizeWeatherText(value),
    PROCORE_WEATHER_GROUND_OPTIONS,
    GROUND_PATTERNS,
  );
}

const CALAMITY_PATTERNS = [
  [/\b(earthquake|quake|seismic|tremor)\b/, "Earthquake"],
  [/\b(fire|wildfire|blaze)\b/, "Fire"],
  [/\b(flash flood\w*|flood\w*|deluge)\b/, "Flash Flood"],
  [/\b(landslide|mudslide|rockslide|slide|slope failure)\b/, "Landslide"],
  [/\b(tornado|twister|funnel cloud)\b/, "Tornado"],
  [/\b(hurricane|typhoon|cyclone|tropical storm)\b/, "Hurricane"],
  [/\b(snow\w*|blizzard|ice storm)\b/, "Snow"],
] as const satisfies readonly (readonly [RegExp, ProcoreWeatherCalamityOption])[];

/**
 * Unlike the other weather fields, an unrecognized calamity falls back to
 * Procore's own "Other" rather than being dropped — a calamity was stated, and
 * losing the fact that one occurred is worse than losing its name. The caller
 * is expected to keep the original wording in the log's comments.
 */
export function normalizeProcoreWeatherCalamity(
  value: unknown,
): ProcoreWeatherCalamityOption | null {
  const normalized = normalizeWeatherText(value);
  if (!normalized) {
    return null;
  }

  return (
    matchWeatherOption(normalized, PROCORE_WEATHER_CALAMITY_OPTIONS, CALAMITY_PATTERNS) ?? "Other"
  );
}

export type ProcoreRecordFamily =
  // ACC Task 3b: "issues" is the Autodesk provider's record family (ACC
  // issues twin observations, punch AND tasks under one family; the local
  // kind is resolved from the issue's subtype — see autodeskSync.registry).
  // The union predates the provider dimension; it is shared by twins, jobs
  // and both registries, so the ACC family extends it rather than forking a
  // second family type. Nothing keys a Record exhaustively on this union.
  | "issues"
  | "forms"
  | "commitment_contracts"
  | "accident_logs"
  | "call_logs"
  | "delay_logs"
  | "delivery_logs"
  | "dumpster_logs"
  | "equipment_logs"
  | "images"
  | "inspection_logs"
  | "manpower_logs"
  | "notes_logs"
  | "observations"
  | "plan_revision_logs"
  | "productivity_logs"
  | "quantity_logs"
  | "safety_violation_logs"
  | "task_items"
  | "visitor_logs"
  | "waste_logs"
  | "weather_logs"
  | "work_logs";

// Two-way sync lifecycle status for `procore_sync_state` docs. Distinct from
// `ProcoreSyncStatus` (the one-way push-only status stamped on the LedAI
// record itself) — this tracks the bidirectional relationship between a
// LedAI record and its Procore twin, including pull-side states.
export type ProcoreTwoWaySyncStatus =
  | "in_sync"
  | "pending_push"
  | "pushing"
  | "pulling"
  | "push_failed"
  | "pull_failed"
  | "deleted_in_procore"
  | "deleted_in_ledai";

// Which side a `procore_sync_state` doc's most recent write originated from —
// LedAI pushing a local edit, or a pull importing/merging a Procore-side change.
/**
 * Where a record was born. "autodesk" (ACC Task 5) marks a record IMPORTED from
 * an ACC issue — distinct from "procore" so no Procore badge or row affordance
 * ever claims an ACC record came from Procore.
 */
export type ProcoreSyncOrigin = "ledai" | "procore" | "autodesk";

export type ProcoreCatalogFamily =
  | "assignable_users"
  | "companies"
  | "locations"
  | "observation_types"
  | "project_users"
  | "task_item_categories"
  | "trades"
  | "vendors";

export type ProcoreReferenceFamily =
  | ProcoreCatalogFamily
  | "commitment_contracts";

export interface ProcoreRemoteReference {
  family: ProcoreReferenceFamily;
  remoteId: string;
  label: string;
  projectId: number;
  code?: string;
  matchedBy?: "id" | "exact" | "contains" | "fallback" | "derived" | "manual";
}

export type AssigneeWorkIntent =
  | "field_execution"
  | "project_coordination"
  | "commercial_admin"
  | "safety_quality"
  | "executive_decision"
  | "general";

export type AssigneeArtifactType = "observation" | "task" | "punch";

/**
 * Recency-weighted assignment feedback attached to a candidate at read time.
 * It is deliberately not persisted inside the responsibility index: that index
 * is rebuilt from Procore and assignment learning has a different lifecycle.
 */
export interface AssigneeContextualHistory {
  overallCount: number;
  overallWeight: number;
  sameProjectCount: number;
  sameProjectWeight: number;
  byIntentCount: Partial<Record<AssigneeWorkIntent, number>>;
  byIntent: Partial<Record<AssigneeWorkIntent, number>>;
  sameProjectByIntentCount: Partial<Record<AssigneeWorkIntent, number>>;
  sameProjectByIntent: Partial<Record<AssigneeWorkIntent, number>>;
  byArtifactIntentCount: Record<string, number>;
  byArtifactIntent: Record<string, number>;
  sameProjectByArtifactIntentCount: Record<string, number>;
  sameProjectByArtifactIntent: Record<string, number>;
  lastAssignedAt?: string;
}

export interface AssigneeScoreComponents {
  identity: number;
  title: number;
  vendor: number;
  company: number;
  trade: number;
  roleFit: number;
  history: number;
  total: number;
  workIntent: AssigneeWorkIntent;
  /**
   * Explicit role + company target recovered from the structured assignee or
   * record context (for example, "president of ABC Electrical"). This is
   * deterministic directory evidence, not an inferred role preference.
   */
  explicitTitleMention?: string;
  roleFitReason?: string;
  contextualAssignmentCount?: number;
}

export interface ProcoreResponsibilityCandidate {
  fileReference?: import("./fileMatching").FileMatchingReference;
  source?: AssigneeSource;
  remoteId: string;
  projectUserRemoteId?: string;
  /**
   * Procore login_information id for this person. Distinct from `remoteId`
   * (assignable-user/contact id) and `projectUserRemoteId` (project-user id).
   * Punch items assign via `login_information_ids`, so this — not `remoteId` —
   * is what {@link getPunchAssigneeUserIds} sends. Absent when the directory
   * has no login_information for the user.
   */
  loginInformationRemoteId?: string;
  vendorId?: string;
  name: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  company?: string;
  vendorLabel?: string;
  /** Legacy flat per-user/company preference; contextual history supersedes it. */
  assignmentFrequency?: number;
  contextualHistory?: AssigneeContextualHistory;
  scoreComponents?: AssigneeScoreComponents;
  score?: number;
  confidencePercent?: number;
  matchedBy?:
    | "exact_name"
    | "name_tokens"
    | "job_title"
    | "vendor"
    | "manual_selection"
    | AssigneeDistributionCandidate["matchedBy"];
}

export interface ProcoreResponsibilityVendorMatch {
  remoteId: string;
  label: string;
  company?: string;
  tradeName?: string;
  abbreviatedName?: string;
}

export type AssigneeResolutionState =
  | "empty"
  | "suggest_distribution"
  | "auto_selected"
  | "manual_selected";

export interface AssigneeDistributionCandidate {
  source?: AssigneeSource;
  remoteId: string;
  projectUserRemoteId?: string;
  name: string;
  company?: string;
  vendorId?: string;
  tradeName?: string;
  jobTitle?: string;
  scoreComponents?: AssigneeScoreComponents;
  confidencePercent: number;
  matchedBy: "name" | "name_company" | "name_trade" | "trade_company" | "history" | "manual";
  evidence: {
    nameMention?: string;
    companyMention?: string;
    tradeMention?: string;
    matchedCommitmentIds?: string[];
    assignmentFrequency?: number;
    workIntent?: AssigneeWorkIntent;
    roleFitScore?: number;
    roleFitReason?: string;
    contextualAssignmentCount?: number;
    contextualHistoryScore?: number;
    explicitTitleMention?: string;
  };
}

export interface ProcoreAssigneeResolution {
  state?: AssigneeResolutionState;
  status: "resolved" | "ambiguous" | "unresolved";
  selectedCandidate?: ProcoreResponsibilityCandidate;
  selectedAssigneeRemoteId?: string;
  distribution?: AssigneeDistributionCandidate[];
  topCandidates?: ProcoreResponsibilityCandidate[];
  matchedVendor?: ProcoreResponsibilityVendorMatch;
  matchedCommitmentIds?: string[];
  originalState?: Exclude<AssigneeResolutionState, "manual_selected">;
  reason?: string;
  /**
   * Manpower Company only (LED-724): the company name the WALK said, written
   * by the directory matcher when no directory company matched and it kept
   * that name in `fieldValues.vendor`. It is the provenance of the text: while
   * Company still reads this name it is "heard, not in the directory"; once a
   * reviewer types something else, the text is theirs (see
   * `unresolvedManpowerCompanyText`). Absent on every record the matcher did
   * not keep a name for, and on every record written before LED-724.
   */
  heardName?: string;
  /** Which company directory `heardName` was checked against, when known. */
  directoryProvider?: "procore" | "autodesk" | "files";
  /**
   * With `heardName`: the heard name matched MORE THAN ONE directory company
   * about equally well. The leader cleared the auto-select bar and only a
   * runner-up within the margin stopped the matcher choosing it, e.g.
   * "W&J HVAC Solutions" when the directory holds it with and without "LLC"
   * (one name to the normalizer, both 99). The name IS in the directory, more
   * than once, so the review card must not say it isn't. Absent for a weak tie
   * (`close_competition` at any score: "Red Electrical" vs ABC 50 / Bright 48),
   * which really is not in the directory, and on records written before it.
   */
  heardMatchesSeveral?: boolean;
}

export interface ProcoreRecordSyncState {
  status: ProcoreSyncStatus;
  projectId?: number;
  family?: ProcoreRecordFamily;
  remoteId?: string;
  /**
   * Procore's HUMAN-facing number for the record — what a super reads out on
   * site and types into a search box. Distinct from `remoteId`, which is the
   * API's own primary key and means nothing to anybody.
   *
   * The console's `#` column printed `remoteId` under a `#` heading, which is
   * an id wearing a number's label (user, 2026-09-10). Absent until a publish
   * or a mirror pull has seen the record, so a row synced before this existed
   * shows no number rather than a wrong one.
   */
  remoteNumber?: string;
  remoteUrl?: string;
  idempotencyKey?: string;
  attemptedAt?: Date;
  syncedAt?: Date;
  /**
   * Last time a two-way PULL applied (or confirmed) Procore's copy of this
   * record onto the LedAI one. Distinct from `syncedAt`, which only ever
   * tracks the outbound push. Written by procoreTwoWaySync.service.ts.
   */
  lastPulledAt?: Date;
  lastError?: string;
  /**
   * This `failed` status was written by the TWO-WAY engine mirroring a parked
   * twin (see `mirrorTwinStatusToRecord`), not by a one-way publish.
   *
   * It exists so the mirror can be non-clobbering: only a failure IT set may
   * be cleared back to `synced` when the twin recovers. A genuine one-way
   * `failed`/`blocked`/`partial` — "no serializer configured", "rate limited",
   * a publish 4xx — belongs to the publish pipeline and must be left alone.
   */
  twoWayFailure?: boolean;
  attachmentRemoteIds?: string[];
  /**
   * Automatic-retry bookkeeping, written by the nightly retry sweep.
   *
   * `retryClass` is the whole decision: only `transient` work is pushed again
   * on a timer. `config`, `terminal` and `awaiting_user` are states a HUMAN
   * has to resolve — a missing Procore observation type, a twin someone
   * deleted, an assignee nobody picked — and retrying them nightly would
   * spend the rate limit that causes the transient failures in the first
   * place. See `procoreSyncRetry.ts` for how a `lastError` maps to a class.
   *
   * All three are absent on every record written before the sweep existed;
   * `shouldRetryNow` classifies from `lastError` when they are.
   */
  retryClass?: ProcoreRetryClass;
  /** Automatic attempts made so far. Stops at `MAX_TRANSIENT_ATTEMPTS`. */
  attempts?: number;
  /** Earliest moment the sweep may try again — the backoff. */
  nextAttemptAt?: Date;
}

export interface ProcoreMediaSyncState {
  status: ProcoreSyncStatus;
  projectId: number;
  remoteId?: string;
  remoteUrl?: string;
  checksum?: string;
  attemptedAt?: Date;
  syncedAt?: Date;
  retryCount?: number;
  lastError?: string;
  /** See `ProcoreRecordSyncState.retryClass`. */
  retryClass?: ProcoreRetryClass;
  /** Earliest moment the sweep may upload this again — the backoff. */
  nextAttemptAt?: Date;
}

export interface ProcoreVisitSyncState {
  status: ProcoreSyncStatus;
  projectId: number;
  lastAttemptedAt?: Date;
  /** A provider-named cooldown, shared by durable review recovery. */
  nextAttemptAt?: Date;
  lastSyncedAt?: Date;
  pendingRecords?: number;
  syncedRecords?: number;
  blockedRecords?: number;
  failedRecords?: number;
  mediaPending?: number;
  mediaSynced?: number;
  mediaFailed?: number;
  /** Media deliberately not sent to Procore — currently video, which the
   *  images endpoint cannot accept. Never counted as a failure. */
  mediaSkipped?: number;
  lastError?: string;
}

export interface ProcoreDailyLogFieldDefinition {
  key: string;
  label: string;
  type: ProcoreDailyLogFieldType;
  placeholder?: string;
  /**
   * Procore visibly marks this field with a red star in the create/edit table
   * for this log category (transcribed from the 2026-08-15 screenshot set —
   * see docs/superpowers/specs/2026-08-15-daily-log-required-fields-review-design.md).
   * Drives the review UI star/legend and the accept-time validator; NOT a
   * dynamic read of project-level Procore configuration.
   */
  requiredInProcore?: boolean;
}

export interface ProcoreDailyLogCategoryDefinition {
  label: string;
  shortLabel?: string;
  description: string;
  scope: ProcoreDailyLogScope;
  titleFieldKeys: string[];
  cardFieldKeys: string[];
  fields: ProcoreDailyLogFieldDefinition[];
}

interface ProcoreDailyLogFieldOptions {
  requiredInProcore?: boolean;
}

const text = (
  key: string,
  label: string,
  placeholder?: string,
  options?: ProcoreDailyLogFieldOptions,
): ProcoreDailyLogFieldDefinition => ({
  key,
  label,
  type: "text",
  placeholder,
  ...(options?.requiredInProcore ? { requiredInProcore: true } : {}),
});

const textarea = (
  key: string,
  label: string,
  placeholder?: string,
  options?: ProcoreDailyLogFieldOptions,
): ProcoreDailyLogFieldDefinition => ({
  key,
  label,
  type: "textarea",
  placeholder,
  ...(options?.requiredInProcore ? { requiredInProcore: true } : {}),
});

const number = (
  key: string,
  label: string,
  placeholder?: string,
  options?: ProcoreDailyLogFieldOptions,
): ProcoreDailyLogFieldDefinition => ({
  key,
  label,
  type: "number",
  placeholder,
  ...(options?.requiredInProcore ? { requiredInProcore: true } : {}),
});

const booleanField = (
  key: string,
  label: string,
  options?: ProcoreDailyLogFieldOptions,
): ProcoreDailyLogFieldDefinition => ({
  key,
  label,
  type: "boolean",
  ...(options?.requiredInProcore ? { requiredInProcore: true } : {}),
});

const time = (
  key: string,
  label: string,
  placeholder?: string,
  options?: ProcoreDailyLogFieldOptions,
): ProcoreDailyLogFieldDefinition => ({
  key,
  label,
  type: "time",
  placeholder,
  ...(options?.requiredInProcore ? { requiredInProcore: true } : {}),
});

const date = (
  key: string,
  label: string,
  placeholder?: string,
  options?: ProcoreDailyLogFieldOptions,
): ProcoreDailyLogFieldDefinition => ({
  key,
  label,
  type: "date",
  placeholder,
  ...(options?.requiredInProcore ? { requiredInProcore: true } : {}),
});

export const PROCORE_DAILY_LOG_CATEGORY_ORDER: ProcoreDailyLogCategory[] = [
  "weather",
  "delays",
  "manpower",
  "notes",
  "visitors",
  "phone_calls",
  "inspections",
  "deliveries",
  "dumpsters",
  "work_logs",
  "plan_revisions",
  "productivity",
  "safety_violations",
  "accidents",
  "equipment",
  "quantities",
  "waste",
  "photos",
  "observations",
];

/**
 * Procore requires `num_hours` on a manpower log, but a superintendent states a
 * headcount — "four electricians on site" — not a shift length. Without a
 * default the record is unpublishable and Accept is blocked on a field the user
 * was never going to say out loud.
 *
 * Fill the standard working day when the model left Hours blank, so the entry
 * publishes and Total Hours reads `workers × 8` (4 electricians → 32). This is
 * a deterministic backstop in the same spirit as `deriveWeatherFieldsFromText`:
 * it only ever fills a blank, and the reviewer can still edit Hours on the card.
 *
 * An explicit `0` is a real value — a crew that logged no hours — and is never
 * overridden. `totalHours` is deliberately NOT written: Procore computes
 * `man_hours` itself, the sync registry drops the key, and every surface
 * derives it from workers × hours.
 */
export const MANPOWER_DEFAULT_HOURS_PER_WORKER = 8;

export function applyManpowerHoursDefault(
  fieldValues: Record<string, ProcoreDailyLogFieldValue>,
): Record<string, ProcoreDailyLogFieldValue> {
  const raw = fieldValues.hours;
  const blank =
    raw === undefined || raw === null || (typeof raw === "string" && !raw.trim());
  if (!blank) {
    // The model sometimes emits a stringy count. Coerce it so the card's
    // preview and the published num_hours cannot disagree.
    if (typeof raw === "string") {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) return { ...fieldValues, hours: parsed };
    }
    return { ...fieldValues };
  }
  return { ...fieldValues, hours: MANPOWER_DEFAULT_HOURS_PER_WORKER };
}

export const PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS: Record<
  ProcoreDailyLogCategory,
  ProcoreDailyLogCategoryDefinition
> = {
  weather: {
    label: "Observed Weather",
    shortLabel: "Weather",
    description: "Track on-site weather conditions that affected the day.",
    scope: "daily_log",
    titleFieldKeys: ["title", "sky", "temperature"],
    cardFieldKeys: ["sky", "temperature", "wind", "ground", "comments"],
    fields: [
      time("time", "Time Observed", undefined, { requiredInProcore: true }),
      text("sky", "Sky"),
      text("temperature", "Temperature"),
      text("wind", "Wind"),
      text("ground", "Ground"),
      text("calamity", "Calamity"),
      textarea("comments", "Comments"),
    ],
  },
  delays: {
    label: "Delays",
    description: "Track what caused work delays and how long they lasted.",
    scope: "daily_log",
    titleFieldKeys: ["title", "delayType", "location"],
    cardFieldKeys: ["delayType", "startTime", "endTime", "comments"],
    fields: [
      text("delayType", "Delay Type"),
      time("startTime", "Start Time"),
      time("endTime", "End Time"),
      text("status", "Status"),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  manpower: {
    label: "Manpower",
    description: "Track companies, workers, and hours on site.",
    scope: "daily_log",
    titleFieldKeys: ["title", "vendor"],
    cardFieldKeys: ["vendor", "workers", "hours", "comments"],
    fields: [
      text("vendor", "Company"),
      number("workers", "Workers", undefined, { requiredInProcore: true }),
      number("hours", "Hours", undefined, { requiredInProcore: true }),
      number("totalHours", "Total Hours"),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  notes: {
    label: "Notes",
    description: "General project notes that do not fit another section.",
    scope: "daily_log",
    titleFieldKeys: ["title", "comment", "location"],
    cardFieldKeys: ["comment", "location", "issue"],
    fields: [
      textarea("comment", "Comment"),
      text("location", "Location"),
      booleanField("issue", "Issue"),
    ],
  },
  visitors: {
    label: "Visitors",
    description: "Track site visitors and the purpose of their visit.",
    scope: "daily_log",
    titleFieldKeys: ["title", "subject", "location"],
    cardFieldKeys: ["subject", "startTime", "endTime", "details"],
    fields: [
      text("subject", "Visitor"),
      time("startTime", "Start Time", undefined, { requiredInProcore: true }),
      time("endTime", "End Time", undefined, { requiredInProcore: true }),
      text("location", "Location"),
      textarea("details", "Details"),
    ],
  },
  phone_calls: {
    label: "Phone Calls",
    shortLabel: "Phone Calls",
    description: "Record important project phone calls and participants.",
    scope: "daily_log",
    titleFieldKeys: ["title", "subjectTo", "subjectFrom"],
    cardFieldKeys: ["subjectTo", "subjectFrom", "startTime", "endTime", "description"],
    fields: [
      text("subjectTo", "To"),
      text("subjectFrom", "From"),
      time("startTime", "Start Time", undefined, { requiredInProcore: true }),
      time("endTime", "End Time", undefined, { requiredInProcore: true }),
      text("location", "Location"),
      textarea("description", "Description"),
    ],
  },
  inspections: {
    label: "Inspections",
    description: "Capture inspections, who performed them, and outcomes.",
    scope: "daily_log",
    titleFieldKeys: ["title", "inspectionType", "area"],
    cardFieldKeys: ["inspectionType", "inspectorName", "area", "startTime", "comments"],
    fields: [
      text("inspectionType", "Inspection Type"),
      text("inspectingEntity", "Inspecting Entity"),
      text("inspectorName", "Inspector Name"),
      text("area", "Area"),
      time("startTime", "Start Time", undefined, { requiredInProcore: true }),
      time("endTime", "End Time", undefined, { requiredInProcore: true }),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  deliveries: {
    label: "Deliveries",
    description: "Track materials and packages delivered to the project.",
    scope: "daily_log",
    titleFieldKeys: ["title", "deliveryFrom", "contents"],
    cardFieldKeys: ["deliveryFrom", "contents", "quantity", "unit", "time", "trackingNumber", "comments"],
    fields: [
      text("deliveryFrom", "Delivery From"),
      text("contents", "Contents"),
      number("quantity", "Quantity"),
      text("unit", "Unit"),
      time("time", "Time", undefined, { requiredInProcore: true }),
      text("trackingNumber", "Tracking Number"),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  dumpsters: {
    label: "Dumpsters",
    description: "Track dumpster swaps, hauls, and vendor activity.",
    scope: "daily_log",
    titleFieldKeys: ["title", "vendor", "location"],
    cardFieldKeys: ["vendor", "quantityDelivered", "quantityRemoved", "status", "comments"],
    fields: [
      text("vendor", "Company"),
      text("ticketNumber", "Ticket Number"),
      number("quantityDelivered", "Delivered", undefined, { requiredInProcore: true }),
      number("quantityRemoved", "Removed", undefined, { requiredInProcore: true }),
      text("status", "Status"),
      time("time", "Time"),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  work_logs: {
    label: "Scheduled Work",
    shortLabel: "Work Logs",
    description: "Track planned work resources, attendance, and labor.",
    scope: "daily_log",
    titleFieldKeys: ["title", "resourceName", "location"],
    cardFieldKeys: ["resourceName", "workers", "hours", "showed", "comments"],
    fields: [
      text("resourceName", "Resource"),
      number("workers", "Workers", undefined, { requiredInProcore: true }),
      number("hours", "Hours", undefined, { requiredInProcore: true }),
      booleanField("showed", "Showed Up"),
      number("hourlyRate", "Hourly Rate"),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  plan_revisions: {
    label: "Plan Revisions",
    description: "Track drawing or plan revision activity impacting the day.",
    scope: "daily_log",
    titleFieldKeys: ["title", "drawing", "revision"],
    cardFieldKeys: ["drawing", "revision", "description", "comments"],
    fields: [
      text("drawing", "Drawing"),
      text("revision", "Revision"),
      text("issuedBy", "Issued By"),
      time("time", "Time"),
      textarea("description", "Description"),
      textarea("comments", "Comments"),
    ],
  },
  productivity: {
    label: "Productivity",
    description: "Track production metrics and output for the day.",
    scope: "daily_log",
    titleFieldKeys: ["title", "resourceName", "location"],
    cardFieldKeys: ["resourceName", "quantity", "unit", "hours", "comments"],
    fields: [
      text("resourceName", "Resource"),
      number("quantity", "Quantity"),
      text("unit", "Unit"),
      number("hours", "Hours"),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  safety_violations: {
    label: "Safety Violations",
    description: "Capture safety issues, notices, and compliance follow-up.",
    scope: "daily_log",
    titleFieldKeys: ["title", "subject", "issuedTo"],
    cardFieldKeys: ["subject", "issuedTo", "time", "complianceDue", "comments"],
    fields: [
      textarea("subject", "Subject"),
      text("issuedTo", "Issued To"),
      textarea("safetyNotice", "Safety Notice"),
      time("time", "Time", undefined, { requiredInProcore: true }),
      date("complianceDue", "Compliance Due"),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  accidents: {
    label: "Accidents",
    description: "Track accidents, who was involved, and where they happened.",
    scope: "daily_log",
    titleFieldKeys: ["title", "involvedName", "involvedCompany"],
    cardFieldKeys: ["involvedName", "involvedCompany", "time", "location", "comments"],
    fields: [
      text("involvedName", "Involved Name"),
      text("involvedCompany", "Involved Company"),
      time("time", "Time", undefined, { requiredInProcore: true }),
      text("location", "Location"),
      textarea("comments", "Comments"),
    ],
  },
  equipment: {
    label: "Equipment",
    description: "Track equipment use, inspections, and idle time.",
    scope: "daily_log",
    titleFieldKeys: ["title", "equipmentName", "location"],
    cardFieldKeys: ["equipmentName", "hoursOperating", "hoursIdle", "inspected", "notes"],
    fields: [
      text("equipmentName", "Equipment"),
      number("hoursOperating", "Hours Operating"),
      number("hoursIdle", "Hours Idle"),
      booleanField("inspected", "Inspected"),
      time("inspectionTime", "Inspection Time"),
      text("location", "Location"),
      textarea("notes", "Notes"),
    ],
  },
  quantities: {
    label: "Quantities",
    description: "Track installed or used material quantities for the day.",
    scope: "daily_log",
    titleFieldKeys: ["title", "description", "location"],
    cardFieldKeys: ["description", "quantity", "unit", "location"],
    fields: [
      textarea("description", "Description"),
      number("quantity", "Quantity"),
      text("unit", "Unit"),
      text("location", "Location"),
    ],
  },
  waste: {
    label: "Waste",
    description: "Track waste material, disposal method, and quantity.",
    scope: "daily_log",
    titleFieldKeys: ["title", "material", "disposalLocation"],
    cardFieldKeys: ["material", "quantity", "methodOfDisposal", "disposalLocation", "description"],
    fields: [
      text("material", "Material"),
      time("time", "Time", undefined, { requiredInProcore: true }),
      number("quantity", "Approximate Quantity", undefined, { requiredInProcore: true }),
      text("methodOfDisposal", "Method of Disposal"),
      text("disposalLocation", "Disposal Location"),
      text("location", "Location"),
      textarea("description", "Description"),
    ],
  },
  photos: {
    label: "Photos",
    description: "Photo-only documentation tied to the daily log.",
    scope: "daily_log",
    titleFieldKeys: ["title", "description", "location"],
    cardFieldKeys: ["description", "takenAt", "location"],
    fields: [
      textarea("description", "Description"),
      time("takenAt", "Taken At"),
      text("location", "Location"),
    ],
  },
  observations: {
    label: "Observations",
    description: "Separate project observations that need follow-up.",
    scope: "observation",
    titleFieldKeys: ["title", "location"],
    cardFieldKeys: ["description", "type", "priority", "status", "dueDate"],
    fields: [
      text("type", "Type"),
      textarea("description", "Description"),
      text("priority", "Priority"),
      text("status", "Status"),
      date("dueDate", "Due Date"),
      text("assignee", "Assignee"),
      text("trade", "Trade"),
      text("location", "Location"),
    ],
  },
};

export const PROCORE_CATEGORY_FAMILY_MAP: Record<
  ProcoreDailyLogCategory,
  ProcoreRecordFamily
> = {
  weather: "weather_logs",
  delays: "delay_logs",
  manpower: "manpower_logs",
  notes: "notes_logs",
  visitors: "visitor_logs",
  phone_calls: "call_logs",
  inspections: "inspection_logs",
  deliveries: "delivery_logs",
  dumpsters: "dumpster_logs",
  work_logs: "work_logs",
  plan_revisions: "plan_revision_logs",
  productivity: "productivity_logs",
  safety_violations: "safety_violation_logs",
  accidents: "accident_logs",
  equipment: "equipment_logs",
  quantities: "quantity_logs",
  waste: "waste_logs",
  photos: "images",
  observations: "observations",
};

export function getProcoreDailyLogCategoryDefinition(
  category: ProcoreDailyLogCategory,
): ProcoreDailyLogCategoryDefinition {
  return PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[category];
}

// Canonical first-state per status-bearing daily-log category. Explicit
// (not data-driven) because each category's status enum + Procore-sync
// mapping differs: observations use Initiated/Ready/Not-accepted/Closed
// (sync normalizeObservationStatusForPublish maps "initiated"); delays/
// dumpsters use Pending/Approved/Rejected (sync normalizeApprovalWorkflowStatus
// maps "pending"; "initiated" would be dropped on sync).
export const DAILY_LOG_DEFAULT_STATUS: Partial<Record<ProcoreDailyLogCategory, string>> = {
  observations: "initiated",
  delays: "pending",
  dumpsters: "pending",
};

export function applyDailyLogStatusDefault(
  category: ProcoreDailyLogCategory,
  fieldValues: Record<string, ProcoreDailyLogFieldValue> | undefined,
): Record<string, ProcoreDailyLogFieldValue> | undefined {
  const def = DAILY_LOG_DEFAULT_STATUS[category];
  if (!def) return fieldValues;
  const fv = fieldValues ?? {};
  const current = fv.status;
  const hasStatus = typeof current === "string" ? current.trim() !== "" : current != null;
  return hasStatus ? fv : { ...fv, status: def };
}

export function getProcoreDailyLogCategoryLabel(
  category: ProcoreDailyLogCategory,
): string {
  return getProcoreDailyLogCategoryDefinition(category).label;
}

/**
 * The mark a category carries in a list — the phone's daily-log sections have
 * worn these since the day-segmented list shipped.
 *
 * They are GLYPHS, not icons: single characters set in the mono face, which is
 * why they survive being moved here. It lives in `shared/` because the console
 * draws the same sections now, and two tables of nineteen symbols would drift
 * the first time a category was added — the same reason the labels and the
 * field definitions are here rather than in either app.
 *
 * `•` is the fallback for a category outside the taxonomy (a legacy value on
 * an old record); nothing here throws for one.
 */
export const PROCORE_DAILY_LOG_CATEGORY_GLYPH: Record<string, string> = {
  photos: "▦",
  notes: "✎",
  deliveries: "▣",
  manpower: "人",
  equipment: "⚙",
  inspections: "✓",
  safety_violations: "△",
  accidents: "⚠",
  weather: "☀",
  delays: "⏱",
  visitors: "◎",
  phone_calls: "☎",
  dumpsters: "▤",
  work_logs: "▤",
  plan_revisions: "✦",
  productivity: "▲",
  quantities: "#",
  waste: "♺",
  observations: "◎",
};

/** The glyph for a category, including one this build has never heard of. */
export function getProcoreDailyLogCategoryGlyph(category: string): string {
  return PROCORE_DAILY_LOG_CATEGORY_GLYPH[category] ?? "•";
}

export function getProcoreDailyLogCategoryScope(
  category: ProcoreDailyLogCategory,
): ProcoreDailyLogScope {
  return getProcoreDailyLogCategoryDefinition(category).scope;
}

export function getProcoreDailyLogFieldDefinition(
  category: ProcoreDailyLogCategory,
  key: string,
): ProcoreDailyLogFieldDefinition | null {
  return (
    getProcoreDailyLogCategoryDefinition(category).fields.find(
      (field) => field.key === key,
    ) || null
  );
}

export function getProcoreRecordFamilyForCategory(
  category: ProcoreDailyLogCategory,
): ProcoreRecordFamily {
  return PROCORE_CATEGORY_FAMILY_MAP[category];
}

/**
 * Parse a clock string into 24-hour {hour, minute}.
 *
 * Procore's daily logs do NOT accept a time string — every time is stored as
 * an integer `*_hour` / `*_minute` pair (verified 2026-08-14 against sandbox
 * project 326057). LedAI normalizes times to a display clock string
 * ("2:14 PM"); this is the bridge between the two.
 *
 * Returns null on anything unparseable. Callers MUST omit the field rather
 * than substituting 0 — 0 is midnight, and a confidently wrong time is worse
 * than an absent one.
 *
 * Lives here (not in procoreDailyLog.service) so the sync registry can use it
 * without importing the service; `parseClockValue` there delegates to this so
 * there is exactly one implementation of the parsing rules.
 */
export function parseProcoreClockValue(
  value: unknown,
): { hour: number; minute: number } | null {
  if (typeof value !== "string") {
    return null;
  }

  const cleaned = value.replace(/\s+/g, " ").trim();
  if (!cleaned) {
    return null;
  }

  const match =
    cleaned.match(/^(\d{1,2})(?::(\d{2}))?\s*([AaPp])(?:[.]?[Mm][.]?)?$/) ||
    cleaned.match(/^(\d{1,2})(?::(\d{2}))?$/);
  if (!match) {
    return null;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2] || "0");
  const meridiem = match[3]?.toUpperCase();

  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  if (meridiem) {
    if (hour < 1 || hour > 12) {
      return null;
    }
    return { hour: meridiem === "P" ? (hour % 12) + 12 : hour % 12, minute };
  }

  if (hour < 0 || hour > 23) {
    return null;
  }

  return { hour, minute };
}

/** Case/space-insensitive key for comparing a Company text to the heard name. */
function heardCompanyKey(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * The manpower Company the walk NAMED that the company directory could not
 * resolve (LED-724), or null.
 *
 * The directory matcher keeps the heard name on the record so the reviewer can
 * see what was said ("Red Electrical"), and records that it chose no directory
 * company (`companyResolution` not resolved, no `vendor` reference) and what
 * it heard (`companyResolution.heardName`). That text is NOT a directory
 * company: nothing may turn it into one implicitly — not publish-time catalog
 * lookup, not a provider's company column. Only a reviewer's pick from the
 * directory resolves it.
 *
 * It is the heard name only while Company still READS the heard name. A
 * Company the reviewer typed (the picker is off while offline, so typing is the
 * only way to correct it there) is the reviewer's answer and is treated as
 * typed text always was (review round 1 of LED-724) — and so is every record
 * written before LED-724, which carries no `heardName`: a reviewer who typed a
 * company over an unresolved match left exactly that shape behind.
 *
 * Null for every other shape: a matched or hand-picked company (it carries a
 * reference / a resolved resolution), a project with no directory (no
 * `companyResolution` — the free text IS the company there), an explicit clear
 * (Company null), typed text, and any non-manpower record.
 */
export function unresolvedManpowerCompanyText(item: {
  category?: string | null;
  fieldValues?: Record<string, ProcoreDailyLogFieldValue> | null;
  resolvedReferences?: Record<string, ProcoreRemoteReference | ProcoreRemoteReference[]> | null;
  companyResolution?: Pick<ProcoreAssigneeResolution, "status" | "selectedCandidate" | "heardName"> | null;
}): string | null {
  if (item.category !== "manpower") return null;
  const resolution = item.companyResolution;
  if (!resolution || resolution.status === "resolved" || resolution.selectedCandidate) return null;
  const reference = item.resolvedReferences?.vendor;
  if (Array.isArray(reference) ? reference.length > 0 : !!reference) return null;
  const vendor = item.fieldValues?.vendor;
  if (typeof vendor !== "string" || !vendor.trim()) return null;
  const heard = resolution.heardName;
  if (typeof heard !== "string" || !heard.trim()) return null;
  return heardCompanyKey(vendor) === heardCompanyKey(heard) ? vendor.trim() : null;
}

/** The same heard-name protection for an AI-generated inspecting entity.
 * A manual reference or text edited by the reviewer remains authoritative.
 */
export function unresolvedInspectionCompanyText(item: Parameters<typeof unresolvedManpowerCompanyText>[0]): string | null {
  if (item.category !== "inspections") return null;
  return unresolvedManpowerCompanyText({
    ...item,
    category: "manpower",
    fieldValues: { ...item.fieldValues, vendor: item.fieldValues?.inspectingEntity ?? null },
  });
}

export function getRequiredProcoreDailyLogFields(
  category: ProcoreDailyLogCategory,
): ProcoreDailyLogFieldDefinition[] {
  return getProcoreDailyLogCategoryDefinition(category).fields.filter(
    (field) => field.requiredInProcore === true,
  );
}

/**
 * Field-presence check for one required field. Mirrors what the reviewer can
 * see in the editable fieldValues — publish-time fallbacks do not count.
 * Zero is a valid number because Procore's create rows initialize several
 * required numeric fields to 0.
 */
export function isRequiredProcoreDailyLogFieldValuePresent(
  field: ProcoreDailyLogFieldDefinition,
  value: ProcoreDailyLogFieldValue | undefined,
): boolean {
  switch (field.type) {
    case "number": {
      const parsed =
        typeof value === "number"
          ? value
          : typeof value === "string" && value.trim() !== ""
            ? Number(value.trim())
            : Number.NaN;
      return Number.isFinite(parsed) && parsed >= 0;
    }
    case "boolean":
      return typeof value === "boolean";
    case "time":
      return typeof value === "string" && parseProcoreClockValue(value) !== null;
    default:
      return typeof value === "string" && value.trim() !== "";
  }
}

export function validateRequiredProcoreDailyLogFields(
  category: ProcoreDailyLogCategory,
  fieldValues: Record<string, ProcoreDailyLogFieldValue> | undefined,
): {
  valid: boolean;
  missingFields: ProcoreDailyLogFieldDefinition[];
  invalidFields: ProcoreDailyLogFieldDefinition[];
} {
  const missingFields: ProcoreDailyLogFieldDefinition[] = [];
  const invalidFields: ProcoreDailyLogFieldDefinition[] = [];

  for (const field of getRequiredProcoreDailyLogFields(category)) {
    const value = fieldValues?.[field.key];
    const isBlank =
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "");
    if (isBlank) {
      missingFields.push(field);
    } else if (!isRequiredProcoreDailyLogFieldValuePresent(field, value)) {
      invalidFields.push(field);
    }
  }

  return {
    valid: missingFields.length === 0 && invalidFields.length === 0,
    missingFields,
    invalidFields,
  };
}

export function formatRequiredProcoreFieldsMessage(
  fields: ProcoreDailyLogFieldDefinition[],
): string {
  const labels = fields.map((field) => field.label);
  if (labels.length === 0) return "";
  if (labels.length === 1) return `${labels[0]} is required in Procore.`;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]} are required in Procore.`;
}

// The synced RPC channel serializes a thrown error to a bare message string
// (see SyncedSession._handleRPC in src/lib/sync.ts), so the "typed" review
// validation error is a JSON payload behind a recognizable prefix.
export const PROCORE_REVIEW_VALIDATION_ERROR_PREFIX = "PROCORE_REVIEW_VALIDATION::";

export interface ProcoreReviewValidationErrorPayload {
  category: ProcoreDailyLogCategory;
  fieldKeys: string[];
  message: string;
}

export function encodeProcoreReviewValidationError(
  payload: ProcoreReviewValidationErrorPayload,
): string {
  return `${PROCORE_REVIEW_VALIDATION_ERROR_PREFIX}${JSON.stringify(payload)}`;
}

export function parseProcoreReviewValidationError(
  message: unknown,
): ProcoreReviewValidationErrorPayload | null {
  if (
    typeof message !== "string" ||
    !message.startsWith(PROCORE_REVIEW_VALIDATION_ERROR_PREFIX)
  ) {
    return null;
  }
  try {
    const parsed = JSON.parse(
      message.slice(PROCORE_REVIEW_VALIDATION_ERROR_PREFIX.length),
    ) as ProcoreReviewValidationErrorPayload;
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.message === "string" &&
      Array.isArray(parsed.fieldKeys)
    ) {
      return parsed;
    }
  } catch {
    // fall through
  }
  return null;
}
